<script setup lang="ts">
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import setTheme from '@/utils/themeColors';
import { computed, watch } from 'vue';
import AuthLayout from '@/components/layouts/AuthLayout.vue';
import ToastNotification from '@/components/common/ToastNotification.vue';
import MainLayout from '@/components/layouts/MainLayout.vue';
import BasicLayout from '@/components/layouts/BasicLayout.vue';
import LoadingScreen from '@/components/common/LoadingScreen.vue';
import { cva } from 'class-variance-authority';
import { useRouter } from 'vue-router';
import SettingsLayout from './components/layouts/SettingsLayout.vue';
import { storeToRefs } from 'pinia';
import { i18n } from '@/utils/i18n';
const { language } = storeToRefs(useAuthStore());

if (language.value == 'en' || language.value == 'el') i18n.global.locale.value = language.value;
const env = import.meta.env.VITE_APP_ENV || 'Local';

const router = useRouter();
const authStore = useAuthStore();
setTheme('blue');
const route = useRoute();

watch(
  () => authStore.isSessionValid,
  (value) => {
    if (!value) router.push('/');
  }
);

const isAuthenticatedRoute = computed(() => {
  return Object.keys(route.meta).length > 0 && 'requiresAuth' in route.meta;
});

const hasAuthLayout = computed(() => {
  return Object.keys(route.meta).length > 0 && 'authLayout' in route.meta;
});

const hasBasicLayout = computed(() => {
  return Object.keys(route.meta).length > 0 && 'basicLayout' in route.meta;
});

const hasSettingsLayout = computed(() => {
  return Object.keys(route.meta).length > 0 && 'settingsLayout' in route.meta;
});

const envClasses = cva(
  'fixed bottom-40 px-2 text-white text-sm rounded-tl-md rounded-tr-md rotate-90 origin-bottom-left z-40',
  {
    variants: {
      env: {
        qa: 'bg-red-400',
        staging: 'bg-yellow-400',
        production: 'bg-green-400',
        local: 'bg-gray-400',
      },
    },
    defaultVariants: {
      env: 'local',
    },
  }
);
</script>

<template>
  <LoadingScreen
    v-if="authStore.isLoading"
    class="absolute top-0 right-0 z-50 w-full h-full bg-opacity-60 bg-slate-200" />
  <ToastNotification />
  <div :class="envClasses({ env: env.toLowerCase() })">{{ env }}</div>
  <AuthLayout v-if="hasAuthLayout">
    <template #content>
      <RouterView />
    </template>
  </AuthLayout>
  <BasicLayout v-else-if="isAuthenticatedRoute && hasBasicLayout">
    <template #content>
      <RouterView />
    </template>
  </BasicLayout>
  <SettingsLayout v-else-if="isAuthenticatedRoute && hasSettingsLayout">
    <template #content>
      <RouterView />
    </template>
  </SettingsLayout>
  <MainLayout v-else-if="isAuthenticatedRoute">
    <template #content>
      <RouterView />
    </template>
  </MainLayout>
  <div v-else>
    <RouterView />
  </div>
</template>
